import { Link, useToast } from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { ProcessRead } from "@nens/rana/api-v1";
import { ProcessTagSchemasToJSONTyped } from "@nens/rana/process-types";
import { TenantContext } from "@nens/ranalib";
import { useLocalStorage } from "@nens/react/hooks";

import { useJobQueries, useJobQueryDirectly } from "@/queries/query-job";
import { useProcessByTagQueryDirectly } from "@/queries/query-process";
import { jobIsRunning } from "@/utils/job-is-running";

import { useStartProcess } from "../api/start-process";
import { ProcessRunArgs, TrackedProcessRun } from "../types/process-provider-types";
import { getJobUrl } from "../utils/get-job-url";

const FINISHED_PROCESS_REMOVE_DELAY_MS = 10_000;
const PROCESS_FETCH_ERROR_MESSAGE =
  "An error occurred while fetching the process. Please try again.";
const JOB_FETCH_ERROR_MESSAGE = "An error occurred while fetching the job. Please try again.";

function isFailedStatus(status: string | undefined) {
  return status === "failed" || status === "crashed";
}

export function useProcessProvider() {
  const intl = useIntl();
  const toast = useToast();
  const { mutateAsync: startProcess, errors } = useStartProcess();
  const fetchJob = useJobQueryDirectly();
  const fetchProcessByTag = useProcessByTagQueryDirectly();
  const { tenant } = useContext(TenantContext);

  // All tracked runs in local storage (running + completed + failed) for the dialog.
  // Stored runs can have stale job state from when they were created.
  const [storedTrackedRuns, setStoredTrackedRuns] = useLocalStorage<TrackedProcessRun[]>(
    "tracked-processes-for-" + tenant,
    []
  );

  const trackedJobIds = storedTrackedRuns.map((trackedRun) => trackedRun.job.id);
  const jobQueries = useJobQueries(trackedJobIds);
  const queriedJobsById = jobQueries.reduce((jobsById, query) => {
    if (!query.data) return jobsById;
    jobsById.set(query.data.id, query.data);
    return jobsById;
  }, new Map<string, TrackedProcessRun["job"]>());

  // Merge up-to-date job data from React Query into the stored runs.
  const trackedRuns: TrackedProcessRun[] = storedTrackedRuns
    .map((trackedRun) => ({
      ...trackedRun,
      job: queriedJobsById.get(trackedRun.job.id),
    }))
    .filter((trackedRun): trackedRun is TrackedProcessRun => trackedRun.job !== undefined);

  // Track which job IDs have already had a completion toast so we never double-fire.
  const toastedJobIdsRef = useRef<Set<string>>(new Set());
  const cleanupTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const showErrorToast = useCallback(
    (description: string) => {
      toast({
        title: intl.formatMessage({ id: "error", defaultMessage: "Error" }),
        description,
        status: "error",
      });
    },
    [intl, toast]
  );

  const showCompletedToast = useCallback(
    (trackedRun: TrackedProcessRun) => {
      toast({
        title: intl.formatMessage({
          id: "process.toast.completed.title",
          defaultMessage: "Success!",
        }),
        description: trackedRun.processCompleteMessage,
        status: "success",
        isClosable: true,
      });
    },
    [intl, toast]
  );

  const showFailedToast = useCallback(
    (trackedRun: TrackedProcessRun) => {
      const jobUrl = getJobUrl({ tenant, process: trackedRun });

      toast({
        title: intl.formatMessage({
          id: "process.toast.failed.title",
          defaultMessage: "Process failed",
        }),
        description: (
          <>
            {intl.formatMessage(
              {
                id: "process.toast.failed.description",
                defaultMessage: 'The process "{name}" has failed.',
              },
              { name: trackedRun.job.name }
            )}{" "}
            {/* Note don't use React Router link here because they don't work in toasts */}
            <Link href={jobUrl} textDecoration="underline">
              {intl.formatMessage({
                id: "process.toast.failed.view_details",
                defaultMessage: "View details",
              })}
            </Link>
          </>
        ),
        status: "error",
        duration: null,
        isClosable: true,
      });
    },
    [intl, tenant, toast]
  );

  const scheduleTrackedRunCleanup = useCallback(
    (jobId: string) => {
      if (cleanupTimeoutsRef.current.has(jobId)) return;

      const timeout = setTimeout(() => {
        setStoredTrackedRuns((prev) => prev.filter((trackedRun) => trackedRun.job.id !== jobId));
        cleanupTimeoutsRef.current.delete(jobId);
        toastedJobIdsRef.current.delete(jobId);
      }, FINISHED_PROCESS_REMOVE_DELAY_MS);

      cleanupTimeoutsRef.current.set(jobId, timeout);
    },
    [setStoredTrackedRuns]
  );

  const resolveProcessFromArgs = useCallback(
    async (args: ProcessRunArgs): Promise<ProcessRead | null> => {
      if ("process" in args) return args.process;

      const { data, isError } = await fetchProcessByTag(args.values.tag);
      if (isError) {
        showErrorToast(
          intl.formatMessage({
            id: "process.toast.start_failed_description",
            defaultMessage: PROCESS_FETCH_ERROR_MESSAGE,
          })
        );
        return null;
      }

      return data;
    },
    [fetchProcessByTag, intl, showErrorToast]
  );

  // Fire toasts for newly completed / failed processes and schedule auto-cleanup.
  useEffect(
    function handleFinishedTrackedRuns() {
      trackedRuns.forEach((trackedRun) => {
        const status = trackedRun.job.state.type;
        const jobId = trackedRun.job.id;

        if (jobIsRunning(status)) return;

        if (!toastedJobIdsRef.current.has(jobId)) {
          toastedJobIdsRef.current.add(jobId);

          if (status === "completed") showCompletedToast(trackedRun);
          if (isFailedStatus(status)) showFailedToast(trackedRun);
        }

        scheduleTrackedRunCleanup(jobId);
      });
    },
    [scheduleTrackedRunCleanup, showCompletedToast, showFailedToast, trackedRuns]
  );

  useEffect(() => {
    const cleanupTimeouts = cleanupTimeoutsRef.current;

    return () => {
      cleanupTimeouts.forEach((timeout) => clearTimeout(timeout));
      cleanupTimeouts.clear();
    };
  }, []);

  const startRun = useCallback(
    async (args: ProcessRunArgs): Promise<TrackedProcessRun | null> => {
      const process = await resolveProcessFromArgs(args);
      if (!process) return null;

      const { inputs: jsonInputs, outputs: jsonOutputs } =
        "tag" in args.values ? ProcessTagSchemasToJSONTyped(args.values) : args.values;

      const { jobId } = await startProcess({
        processId: process.id,
        executionRequest: {
          name: args.values.name,
          projectId: args.project.id,
          inputs: jsonInputs,
          outputs: jsonOutputs,
          workflowId: args.workflowId,
        },
      });

      const { data: job, isSuccess } = await fetchJob(jobId);
      if (!isSuccess) {
        showErrorToast(
          intl.formatMessage({
            id: "process.toast.job_start_failed_description",
            defaultMessage: JOB_FETCH_ERROR_MESSAGE,
          })
        );
        return null;
      }

      const trackedRun: TrackedProcessRun = { ...args, process, job };
      setStoredTrackedRuns((prev) => {
        const withoutExisting = prev.filter(
          (storedTrackedRun) => storedTrackedRun.job.id !== trackedRun.job.id
        );
        return [...withoutExisting, trackedRun];
      });
      return trackedRun;
    },
    [fetchJob, intl, resolveProcessFromArgs, setStoredTrackedRuns, showErrorToast, startProcess]
  );

  return {
    trackedRuns,
    startRun,
    errors,
  };
}
